import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Save, User, Phone, Mail, Loader2, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabaseClient";

const PersonalInfo = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({ name: "", phone: "" });
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const getProfile = async () => {
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .single();

        if (error) throw error;
        if (data) setFormData({ name: data.name || "", phone: data.phone || "" });
      } catch (err) {
        console.error("Error fetching profile:", err.message);
      } finally {
        setLoading(false);
      }
    };
    getProfile();
  }, [user]);

  const handleUpdate = async () => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update(formData)
        .eq("id", user.id);

      if (error) throw error;
      
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      alert("Failed to update profile: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) return (
    <div className="flex h-screen items-center justify-center bg-gray-50">
      <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50/50 p-4 md:p-12">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto"
      >
        {/* BACK BUTTON */}
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 text-emerald-600 font-black uppercase text-[10px] tracking-[0.3em] mb-8 group transition-all"
        >
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" /> Profile
        </button>

        {/* MAIN CARD */}
        <div className="bg-white rounded-[48px] p-8 md:p-12 shadow-2xl shadow-emerald-900/5 border border-gray-100 relative overflow-hidden">
          
          <div className="relative z-10">
            <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tighter">Personal Info</h2>
            <p className="text-gray-400 font-medium text-sm mb-10">Update your identity and contact details</p>
            
            <div className="space-y-8">
              {/* NAME INPUT */}
              <div className="space-y-3">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-5 flex items-center gap-2">
                  <User size={12} /> Full Name
                </label>
                <input 
                  type="text"
                  placeholder="Enter your name"
                  className="w-full bg-gray-50 border-2 border-transparent rounded-[24px] p-5 font-bold text-gray-700 outline-none transition-all focus:bg-white focus:border-emerald-500/20 focus:ring-4 ring-emerald-500/5"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>

              {/* PHONE INPUT */}
              <div className="space-y-3">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-5 flex items-center gap-2">
                  <Phone size={12} /> Phone Number
                </label>
                <input 
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  className="w-full bg-gray-50 border-2 border-transparent rounded-[24px] p-5 font-bold text-gray-700 outline-none transition-all focus:bg-white focus:border-emerald-500/20 focus:ring-4 ring-emerald-500/5"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>

              {/* EMAIL READ-ONLY */}
              <div className="space-y-3 opacity-60">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-5 flex items-center gap-2">
                  <Mail size={12} /> Email Address (Private)
                </label>
                <div className="w-full bg-gray-100 rounded-[24px] p-5 font-bold text-gray-500 cursor-not-allowed">
                  {user?.email}
                </div>
              </div>

              {/* SAVE BUTTON */}
              <motion.button 
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={isSaving}
                onClick={handleUpdate}
                className={`w-full relative flex items-center justify-center gap-3 p-6 rounded-[28px] font-black uppercase tracking-widest text-xs transition-all shadow-xl ${
                  showSuccess 
                    ? "bg-emerald-100 text-emerald-600 shadow-emerald-500/10" 
                    : "bg-emerald-500 text-white shadow-emerald-500/30 hover:bg-emerald-600"
                }`}
              >
                {isSaving ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : showSuccess ? (
                  <>
                    <CheckCircle size={18} /> Profile Saved
                  </>
                ) : (
                  <>
                    <Save size={18} /> Save Changes
                  </>
                )}
              </motion.button>
            </div>
          </div>

          {/* BACKGROUND DECOR */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50 rounded-full -mr-16 -mt-16 blur-3xl opacity-50 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-50 rounded-full -ml-16 -mb-16 blur-3xl opacity-50 pointer-events-none" />
        </div>
      </motion.div>

      {/* SUCCESS TOAST */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-8 py-4 rounded-full font-bold text-sm shadow-2xl flex items-center gap-3 z-50"
          >
            <div className="bg-emerald-500 p-1 rounded-full">
                <CheckCircle size={14} className="text-white" />
            </div>
            Data synced with secure server
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PersonalInfo;